// Pure predicates for the two-way wallet reconcile loop. Extracted from
// the frontend `auth-success-handler` as part of the auth-cutover
// (spec 009 FILE 14 Group B — auth orchestration into the SDK).
//
// Both predicates assume the input list is the backend's view of the
// user's wallets (`fetchWallets()` response). The frontend is the
// authoritative source for the CURRENT Turnkey sub-org, derived from
// `useSdkTurnkey().session.organizationId`.

import type { SdkWallet } from '../../../../auth/wallets/wallets-context.js';

/** True iff `wallet` is a Turnkey-managed backend row (embedded OR
 *  imported) whose stored sub-org is populated, not archived, and DOES
 *  NOT match the active session. Such rows are UNREACHABLE from this
 *  session: their key lives in the other sub-org, and so does the
 *  balance. They are reported, never archived — hiding one takes from
 *  its owner the only pointer they have left to it, and the backend
 *  refuses the archive for the same reason.
 *
 *  Defensive guards (each individually sufficient — together they make
 *  the predicate safe to apply blindly):
 *    - `external` wallets are NEVER archived. They're user-controlled
 *      (Phantom / Backpack / etc.), have no Turnkey sub-org, and
 *      "staleness" can't be inferred from sub-org rotation.
 *    - Already-archived rows are never re-archived.
 *    - `turnkeySubOrgId === null` rows are NEVER archived. Pre-backfill
 *      legacy data has nothing to compare against; the migration's
 *      backfill stamps them with the user's current sub-org so they
 *      converge legitimately on the next login.
 */
export function isWalletInAnotherSubOrg(wallet: SdkWallet, activeSubOrgId: string): boolean {
  if (wallet.type !== 'embedded' && wallet.type !== 'imported') return false;
  if (wallet.archivedAt !== null && wallet.archivedAt !== undefined) return false;
  if (typeof wallet.turnkeySubOrgId !== 'string' || wallet.turnkeySubOrgId.length === 0) {
    return false;
  }
  return wallet.turnkeySubOrgId !== activeSubOrgId;
}

/** Filter helper — the subset of `wallets` this session cannot reach.
 *  Equivalent to `wallets.filter(w => isWalletInAnotherSubOrg(w, …))` but
 *  spelled out so call sites read at a glance. */
export function findWalletsInAnotherSubOrg(
  wallets: readonly SdkWallet[],
  activeSubOrgId: string,
): SdkWallet[] {
  return wallets.filter((w): boolean => isWalletInAnotherSubOrg(w, activeSubOrgId));
}
